import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

type FeatureTourType = 
  | 'family_mode'
  | 'schedule_config'
  | 'rewards_shop'
  | 'notifications_setup'
  | 'collaborative_note'
  | 'member_switch';

interface FeatureTourState {
  [key: string]: {
    completed: boolean;
    skipped: boolean;
    completedAt?: Date;
  };
}

interface FeatureTourContextType {
  hasSeenTour: (feature: FeatureTourType) => boolean;
  shouldShowTour: (feature: FeatureTourType) => boolean;
  markTourAsCompleted: (feature: FeatureTourType) => Promise<void>;
  skipTour: (feature: FeatureTourType) => Promise<void>;
  resetTour: (feature: FeatureTourType) => Promise<void>;
  resetAllTours: () => Promise<void>;
}

const FeatureTourContext = createContext<FeatureTourContextType | undefined>(undefined);

export function FeatureTourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tourState, setTourState] = useState<FeatureTourState>({});

  useEffect(() => {
    loadTourState();
  }, [user?.uid]);

  const loadTourState = async () => {
    if (!user?.uid) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setTourState(data.featureTours || {});
      }
    } catch (error) {
      console.error('Error loading feature tours:', error);
    }
  };

  const hasSeenTour = (feature: FeatureTourType): boolean => {
    return tourState[feature]?.completed || tourState[feature]?.skipped || false;
  };

  const shouldShowTour = (feature: FeatureTourType): boolean => {
    return !hasSeenTour(feature);
  };

  const markTourAsCompleted = async (feature: FeatureTourType) => {
    if (!user?.uid) return;

    try {
      const newState = {
        ...tourState,
        [feature]: {
          completed: true,
          skipped: false,
          completedAt: new Date(),
        },
      };

      await setDoc(
        doc(db, 'users', user.uid),
        { featureTours: newState },
        { merge: true }
      );

      setTourState(newState);
    } catch (error) {
      console.error('Error marking tour as completed:', error);
    }
  };

  const skipTour = async (feature: FeatureTourType) => {
    if (!user?.uid) return;

    try {
      const newState = {
        ...tourState,
        [feature]: {
          completed: false,
          skipped: true,
          completedAt: new Date(),
        },
      };

      await setDoc(
        doc(db, 'users', user.uid),
        { featureTours: newState },
        { merge: true }
      );

      setTourState(newState);
    } catch (error) {
      console.error('Error skipping tour:', error);
    }
  };

  const resetTour = async (feature: FeatureTourType) => {
    if (!user?.uid) return;

    try {
      const newState = { ...tourState };
      delete newState[feature];

      await setDoc(
        doc(db, 'users', user.uid),
        { featureTours: newState },
        { merge: true }
      );

      setTourState(newState);
    } catch (error) {
      console.error('Error resetting tour:', error);
    }
  };

  const resetAllTours = async () => {
    if (!user?.uid) return;

    try {
      await setDoc(
        doc(db, 'users', user.uid),
        { featureTours: {} },
        { merge: true }
      );

      setTourState({});
    } catch (error) {
      console.error('Error resetting all tours:', error);
    }
  };

  return (
    <FeatureTourContext.Provider
      value={{
        hasSeenTour,
        shouldShowTour,
        markTourAsCompleted,
        skipTour,
        resetTour,
        resetAllTours,
      }}
    >
      {children}
    </FeatureTourContext.Provider>
  );
}

export function useFeatureTour() {
  const context = useContext(FeatureTourContext);
  if (context === undefined) {
    throw new Error('useFeatureTour must be used within a FeatureTourProvider');
  }
  return context;
}
